/**
 * ControlsPanel.jsx
 *
 * Main control panel for the text embedding visualization app.
 * Provides UI for text input, parameter adjustment (including coloring, environment, and fog), and actions (generate, save, open).
 * Uses Material UI for layout and controls.
 *
 * Copyright (c) 2023-2025, text-embedding-visualization authors.
 * Licensed under the MIT License.
 * Author: [Your Name]
 * Date: [Update as needed]
 */

/**
 * ControlsPanel
 *
 * @component
 * @param {Object} props
 * @param {string} props.inputText - Current input text.
 * @param {Array} props.tokens - Array of token objects.
 * @param {Array} props.chunks - Array of chunk objects (windows).
 * @param {boolean} props.showTokenIds - Whether to display token IDs.
 * @param {function} props.onInputTextChange - Callback for text change.
 * @param {function} props.onToggleShowTokenIds - Toggle showTokenIds.
 * @param {string} props.textPanel_tokenColorMode - Token coloring mode for input text panel.
 * @param {function} props.onChangeTextPanel_tokenColorMode - Callback for input text panel coloring mode change.
 * @param {string} props.displayParam_tokenColorMode - Token coloring mode for display parameters.
 * @param {function} props.onChangeDisplayParam_tokenColorMode - Callback for display parameter coloring mode change.
 * @param {string} props.displayParam_environmentColorMode - Environment (background) color mode for 3D scene.
 * @param {function} props.onChangeDisplayParam_environmentColorMode - Callback for environment color mode change.
 * @param {string} props.displayParam_fogColorMode - Fog color mode for 3D scene.
 * @param {function} props.onChangeDisplayParam_fogColorMode - Callback for fog color mode change.
 * @param {number} props.windowSize - Current window size value.
 * @param {function} props.onWindowSizeChange - Callback for window size change.
 * @param {number} props.overlap - Current overlap value.
 * @param {function} props.onOverlapChange - Callback for overlap change.
 * @param {number} props.embeddingDimensions - Embedding dimension value.
 * @param {function} props.onEmbeddingDimensionsChange - Callback for dimension change.
 * @param {boolean} props.showLabels - Whether to show first-word labels.
 * @param {function} props.onToggleShowLabels - Toggle showLabels.
 * @param {function} props.onGenerateVisualization - Callback for processing action.
 * @param {boolean} props.isLoading - Processing/loading state.
 * @param {boolean} props.isTokenizing - Whether tokenization is in progress.
 * @param {boolean} props.isChunking - Whether chunking is in progress.
 * @param {function} props.setIsChunking - Setter for chunking state.
 * @param {number} props.textSize - Text size for visualization.
 * @param {function} props.onTextSizeChange - Callback for text size change.
 * @param {number} props.dotSize - Dot size for visualization.
 * @param {function} props.onDotSizeChange - Callback for dot size change.
 * @param {number} props.labelOffset - Vertical offset for label display.
 * @param {function} props.onLabelOffsetChange - Callback for label offset change.
 * @param {function} props.getTokenColor - Function to get color for a token id.
 * @param {function} props.getSequenceColor - Function to get color for a token index in sequence.
 * @param {boolean} props.readyToSave - Whether the Save button should be enabled.
 * @param {function} props.handleSave - Callback for when the Save button is pressed.
 * @param {function} [props.onOpenTevJson] - Callback when a TEV JSON file is loaded.
 * @param {string} props.showPathMode - Path display mode (curve, lines, none).
 * @param {function} props.onChangeShowPathMode - Handler for path mode change.
 * @param {string} props.pointStyle - Point style (circle, ring, cloud).
 * @param {function} props.onChangePointStyle - Handler for point style change.
 * @param {boolean} props.showHelpers - Whether to show 3D helpers.
 * @param {function} props.onToggleShowHelpers - Handler for toggling helpers.
 * @param {Array} [props.poems] - Array of poems for selection.
 * @param {number|string} [props.selectedPoemIndex] - Selected poem index.
 * @param {function} [props.onSelectPoem] - Handler for poem selection.
 * @param {function} [props.onEnterVR] - Handler for entering VR mode.
 */

/**
 * @typedef {import('../types/chunk').Chunk} Chunk
 * @typedef {import('../types/token').Token} Token
 * @typedef {import('../types/embedding').Embedding} Embedding
 * @typedef {import('../types/pca').PCA} PCA
 */

import React, { useState } from 'react';
import { PointStyle } from '../objects/CustomPointHelper';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import BuildIcon from '@mui/icons-material/Build';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import Tooltip from '@mui/material/Tooltip';
import PoemSelector from './PoemSelector';
import InputTextPanel from './InputTextPanel';
import DisplayParametersPanel from './DisplayParametersPanel';
import ChunkingParametersPanel from './ChunkingParametersPanel';
import EmbeddingParametersPanel from './EmbeddingParametersPanel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';

/**
 * ControlsPanel component definition
 */
export default function ControlsPanel(props) {
  // Destructure all props for clarity
  // Point style and showPathMode are now controlled by parent (TextEmbeddingVisualizer)
  const {
    inputText,
    tokens,
    chunks,
    showTokenIds,
    onInputTextChange,
    onToggleShowTokenIds,
    textPanel_tokenColorMode,
    onChangeTextPanel_tokenColorMode,
    displayParam_tokenColorMode,
    onChangeDisplayParam_tokenColorMode,
    displayParam_labelColorMode,
    onChangeDisplayParam_labelColorMode,
    displayParam_environmentColorMode,
    onChangeDisplayParam_environmentColorMode,
    displayParam_fogColorMode,
    onChangeDisplayParam_fogColorMode,
    windowSize,
    onWindowSizeChange,
    overlap,
    onOverlapChange,
    embeddingDimensions,
    onEmbeddingDimensionsChange,
    showLabels,
    onToggleShowLabels,
    onGenerateVisualization,
    isLoading,
    isTokenizing,
    isChunking,
    setIsChunking,
    textSize,
    onTextSizeChange,
    dotSize,
    onDotSizeChange,
    labelOffset,
    onLabelOffsetChange,
    showHelpers,
    onToggleShowHelpers,
    poems = [],
    selectedPoemIndex,
    onSelectPoem,
    getTokenColor,
    getSequenceColor,
    readyToSave,
    handleSave,
    onOpenTevJson,
    showPathMode,
    onChangeShowPathMode,
    pointStyle,
    onChangePointStyle,
  } = props;

  const EMBEDDING_DIM_OPTIONS = [1, 2, 3, 4, 8, 16, 32, 64, 128, 256, 512, 1024, 1536];

  const computedChunkCount = chunks?.length ?? 0;

  // State for collapsible sections in the panel
  const [inputTextOpen, setInputTextOpen] = useState(true);
  const [displayParamsOpen, setDisplayParamsOpen] = useState(false);
  const [chunkingParamsOpen, setChunkingParamsOpen] = useState(false);
  const [embeddingParamsOpen, setEmbeddingParamsOpen] = useState(false);
  const [poemSelectorOpen, setPoemSelectorOpen] = useState(true);
  const [showColorTokens, setShowColorTokens] = useState(false);

  // Main panel container
  return (
    <Paper
      elevation={6}
      sx={{
        top: 0,
        left: 0,
        zIndex: 10,
        p: 1,
        borderRadius: 2,
        maxWidth: 400,
        minWidth: 320,
        fontSize: '0.90rem',
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.75)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 120px)', // Full height minus the top controls panel and pca matrix panel
        overflow: 'auto',
      }}
    >
      {/* Title and VR button */}
      <Box display="flex" alignItems="center" mb={.5}>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.25rem', flex: 1 }}>
          Text Embeddings Visualization
        </Typography>
        {/* VR mode button (WebXR) */}
        <Tooltip title="Enter VR mode (WebXR)" arrow>
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={props.onEnterVR}
              aria-label="Enter VR"
              sx={{ ml: 1 }}
              disabled={!props.onEnterVR}
            >
              <ViewInArIcon fontSize="medium" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Instructions */}
      <Box
        mb={.5}
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          backgroundColor: 'rgba(255,255,255,0.85)',
          borderRadius: 2,
          p: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Paste or type your text below, adjust parameters as needed, and click <b>Generate Visualization</b> to explore your data.
        </Typography>
      </Box>

      {/* Poem Selector Panel */}
      <Box
        mb={0.5}
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          backgroundColor: 'rgba(255,255,255,0.85)',
          borderRadius: 2,
          padding: 1.5,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Poem selector header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            Select a Poem{' '}
            <Box
              component="span"
              sx={{
                fontWeight: 400,
                fontSize: '0.75em',
                color: 'text.secondary',
                display: 'inline',
              }}
            >
              [optional]
            </Box>
          </Typography>
          <Box display="flex" alignItems="center">
            <Tooltip
              title="Choose a poem to quickly populate the input text. You can still edit the text after selection."
              placement="top"
              arrow
            >
              <HelpOutlineIcon 
                fontSize="small" 
                sx={{ color: 'text.secondary', cursor: 'pointer', ml: 1 }} />
            </Tooltip>
            {/* Expand/collapse button for poem selector */}
            <IconButton
              size="small"
              onClick={() => setPoemSelectorOpen((open) => !open)}
              aria-label={poemSelectorOpen ? "Collapse" : "Expand"}
              sx={{
                transform: poemSelectorOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s',
                ml: 1,
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>
        <Collapse in={poemSelectorOpen}>
          <PoemSelector
            poems={poems}
            selectedPoemIndex={selectedPoemIndex}
            onSelectPoem={onSelectPoem}
          />
        </Collapse>
      </Box>

      {/* Main controls area (scrollable) */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Input text panel */}
        <InputTextPanel
          inputText={inputText}
          tokens={tokens}
          showTokenIds={showTokenIds}
          onInputTextChange={onInputTextChange}
          onToggleShowTokenIds={onToggleShowTokenIds}
          isTokenizing={isTokenizing}
          inputTextOpen={inputTextOpen}
          setInputTextOpen={setInputTextOpen}
          showColorTokens={showColorTokens}
          setShowColorTokens={setShowColorTokens}
          textPanel_tokenColorMode={textPanel_tokenColorMode}
          onChangeTextPanel_tokenColorMode={onChangeTextPanel_tokenColorMode}
          getTokenColor={getTokenColor}
          getSequenceColor={getSequenceColor}
        />

        {/* Display parameters panel (color, point style, path, etc.) */}
        <DisplayParametersPanel
          displayParamsOpen={displayParamsOpen}
          setDisplayParamsOpen={setDisplayParamsOpen}
          displayParam_tokenColorMode={displayParam_tokenColorMode}
          onChangeDisplayParam_tokenColorMode={onChangeDisplayParam_tokenColorMode}
          displayParam_labelColorMode={displayParam_labelColorMode}
          onChangeDisplayParam_labelColorMode={onChangeDisplayParam_labelColorMode}
          showLabels={showLabels}
          onToggleShowLabels={onToggleShowLabels}
          showPathMode={showPathMode}
          onChangeShowPathMode={onChangeShowPathMode}
          pointStyle={pointStyle}
          onChangePointStyle={onChangePointStyle}
          showHelpers={showHelpers}
          onToggleShowHelpers={onToggleShowHelpers}
          textSize={textSize}
          onTextSizeChange={onTextSizeChange}
          dotSize={dotSize}
          onDotSizeChange={onDotSizeChange}
          labelOffset={labelOffset}
          onLabelOffsetChange={onLabelOffsetChange}
          displayParam_fogColorMode={displayParam_fogColorMode}
          onChangeDisplayParam_fogColorMode={onChangeDisplayParam_fogColorMode}
          displayParam_environmentColorMode={displayParam_environmentColorMode}
          onChangeDisplayParam_environmentColorMode={onChangeDisplayParam_environmentColorMode}
        />

        {/* Chunking parameters panel (window size, overlap) */}
        <ChunkingParametersPanel
          chunkingParamsOpen={chunkingParamsOpen}
          setChunkingParamsOpen={setChunkingParamsOpen}
          isChunking={isChunking}
          computedChunkCount={computedChunkCount}
          windowSize={windowSize}
          onWindowSizeChange={onWindowSizeChange}
          overlap={overlap}
          onOverlapChange={onOverlapChange}
          setIsChunking={setIsChunking}
        />

        {/* Embedding parameters panel (embedding dimensions) */}
        <EmbeddingParametersPanel
          embeddingParamsOpen={embeddingParamsOpen}
          setEmbeddingParamsOpen={setEmbeddingParamsOpen}
          embeddingDimensions={embeddingDimensions}
          onEmbeddingDimensionsChange={onEmbeddingDimensionsChange}
          EMBEDDING_DIM_OPTIONS={EMBEDDING_DIM_OPTIONS}
        />
      </Box>

      {/* Generate visualization button */}
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 0.5 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onGenerateVisualization}
          disabled={isLoading || isTokenizing || isChunking || !chunks || chunks.length === 0}
          fullWidth
          size="medium"
          sx={{ py: 0.5, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          startIcon={<BuildIcon />}
          endIcon={
            <Tooltip title="Process the input text and generate the embedding visualization." arrow>
              <HelpOutlineIcon fontSize="small" sx={{ ml: 0.5, cursor: 'pointer' }} />
            </Tooltip>
          }
        >
          {isLoading ? 'Processing...' : 'Generate Visualization'}
        </Button>
      </Box>

      {/* Save and Open row */}
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1 }}>
        {/* Save button */}
        <Button
          variant="contained"
          color="success"
          size="small"
          sx={{ minWidth: 64, px: 1, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          disabled={!readyToSave}
          onClick={() => {
            console.log('[ControlsPanel] Save button pressed');
            handleSave && handleSave();
          }}
          startIcon={<SaveAltIcon />}
          endIcon={
            <Tooltip title="Save the current visualization as a TEV file for later viewing or sharing." arrow>
              <HelpOutlineIcon fontSize="small" sx={{ ml: 0.5, cursor: 'pointer' }} />
            </Tooltip>
          }
        >
          Save
        </Button>
        {/* Open button for TEV JSON files (disabled for now) */}
        <input
          type="file"
          accept=".json,application/json"
          style={{ display: 'none' }}
          id="open-tev-json-input"
          onChange={e => {
            const file = e.target.files && e.target.files[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = evt => {
              try {
                const data = JSON.parse(evt.target.result);
                if (props.onOpenTevJson) {
                  props.onOpenTevJson(data);
                }
              } catch (err) {
                alert("Invalid JSON file.");
              }
            };
            reader.readAsText(file);
            // Reset input so the same file can be picked again if needed
            e.target.value = '';
          }}
        />
        <Button
          variant="outlined"
          color="primary"
          size="small"
          sx={{ ml: 1, minWidth: 64, px: 1, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          component="label"
          startIcon={<SaveAltIcon />}
          endIcon={
            <Tooltip title="Open a saved TEV JSON file from your computer to restore a previous visualization." arrow>
              <HelpOutlineIcon fontSize="small" sx={{ ml: 0.5, cursor: 'pointer' }} />
            </Tooltip>
          }
          //disabe the button while debugging the open file input
          disabled={true} // Uncomment this line to disable the button while debugging

        >
          Open
          <input
            type="file"
            accept=".json,application/json"
            hidden
            onChange={e => {
              console.log('[ControlsPanel] Open file input changed:', e); // <-- Add this log
              const file = e.target.files && e.target.files[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = evt => {
                try {
                  const data = JSON.parse(evt.target.result);
                  if (props.onOpenTevJson) {
                    props.onOpenTevJson(data);
                  }
                } catch (err) {
                  alert("Invalid JSON file.");
                }
              };
              reader.readAsText(file);
              // Reset input so the same file can be picked again if needed
              e.target.value = '';
            }}
          />
        </Button>
      </Box>
    </Paper>
  );
}
